import React from "react";
import { useGetCbtUsageQuery } from "../../reactWrapper/Redux/apiSlice";

// How much of a limit is used, as a whole percentage from 0 to 100
function percentUsed(used, limit) {
  if (limit <= 0) {
    return 100; // a limit of 0 means nothing may run: show it as full
  }
  return Math.min(100, Math.round((used / limit) * 100));
}

// The bar colour: green at the start, yellow from half way, red near the end
function barColour(percent) {
  if (percent >= 80) {
    return "bg-red-500";
  }
  if (percent >= 50) {
    return "bg-amber-400";
  }
  return "bg-emerald-500";
}

/** One limit: a title, "x of y", and a coloured progress bar. */
function Meter({ title, model, used, limit, text }) {
  const percent = percentUsed(used, limit);
  const isFull = used >= limit;

  return (
    <div className={`min-w-0 rounded-xl border p-3 ${isFull ? "border-red-200 bg-red-50" : "bg-card"}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-brand-navy">
          {title} <span className="font-normal text-muted-foreground">· {model}</span>
        </span>
        <span className={`text-xs ${isFull ? "font-semibold text-red-700" : "text-muted-foreground"}`}>
          {isFull ? "Limit reached" : text}
        </span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-brand-navy-100">
        <div className={`h-full rounded-full ${barColour(percent)}`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

/**
 * The last 24 hours of AI usage against the limits: Claude's spending, and
 * each checker model's checks. Refreshes every minute, and after every run action.
 */
export default function UsageBar() {
  const { data } = useGetCbtUsageQuery(undefined, { pollingInterval: 60000 });
  if (!data) {
    return null;
  }

  const writer = data.writer;

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Meter
        title="Writer"
        model={writer.model}
        used={writer.spentUsd}
        limit={writer.limitUsd}
        text={`$${writer.spentUsd.toFixed(2)} of $${writer.limitUsd.toFixed(2)} in 24 h`}
      />

      {data.checkers.map((checker) => (
        <Meter
          key={checker.model}
          title="Checker"
          model={checker.model}
          used={checker.used}
          limit={checker.limit}
          text={`${checker.used} of ${checker.limit} checks in 24 h`}
        />
      ))}
    </div>
  );
}
